import './OkulyonetimToolbar.scss'

import { CaretDownFilled, HeartOutlined, LogoutOutlined, MailFilled, ShoppingCartOutlined } from '@ant-design/icons'
import { Select } from 'antd'
import { useSubelerQuery } from 'gql/GeneratedGraphqlTypes'
import useMediaQuery from 'hooks/useMediaQuery'
import icon from 'images/okulyonetim/cagrimerkezi-icon2.png'
import React, { useContext, useEffect, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { AppRoutes } from '../../../App'
import AppContext from '../../../context/AppContext'
import { DEFAULT_API_TOKEN } from '../../../utils/const'
import { clearLoginSession } from '../../../utils/helpers'
import { AuthState } from '../../../utils/types'
import Spinner from '../../spinner/Spinner'
import { ReactComponent as CampusIcon } from './toolbar-icons/Icon.svg'
import { ReactComponent as UserIcon } from './toolbar-icons/Vector.svg'

interface Props {
  logo: string
  isim: string
  yetkili_ad: string | null | undefined
  yetkili_soyad: string | null | undefined
  yetkili_numara: string | null | undefined
  renk1: string | null | undefined
  renk2: string | null | undefined
}

const OkulyonetimToolbar: React.FC<Props> = ({
  logo,
  isim,
  renk1,
  renk2,
  yetkili_ad,
  yetkili_soyad,
  yetkili_numara,
}) => {
  const { setSinif, sinif, token, setToken, setAuthState, setGlobalNotification, setEmail, okul } =
    useContext(AppContext)
  const navigate = useNavigate()
  const isMobile = useMediaQuery(768)
  // Query only if the token exists
  const { data, loading, error } = useSubelerQuery({
    skip: token === '' || token === DEFAULT_API_TOKEN,
  })
  useEffect(() => {
    if (!data?.profil) return

    // Öğretmen değilse
    if (!data.profil.is_ogretmen) {
      setGlobalNotification({
        type: 'warning',
        message: 'Yetkisiz Erişim',
        description: 'Öğretmen hesabınız yok. Anasayfaya yönlendiriliyorsunuz...',
      })
      navigate('/')
      return
    }

    // Öğretmense ama kendi okulunda değilse
    if (Array.isArray(data.profil.subeler) && data.profil.subeler[0]?.okul_id !== okul?.id) {
      setGlobalNotification({
        type: 'warning',
        message: 'Yetkisiz Erişim',
        description: 'Bu okula atanmış öğretmen değilsiniz. Anasayfaya yönlendiriliyorsunuz...',
      })
      navigate('/')
      return
    }
  }, [data, okul])

  const subeOptions = useMemo(() => {
    return (
      data?.profil?.subeler?.map((sube) => ({
        value: sube?.id,
        label: sube?.isim,
      })) || []
    )
  }, [data])

  useEffect(() => {
    if (!sinif && subeOptions.length > 0 && setSinif) {
      setSinif(subeOptions[0].value!) // İlk yüklemede sadece bir kez çağrılır
    }
    setEmail(data?.profil?.email || '')
  }, [subeOptions, sinif, setSinif])

  if (!token) {
    return null // Eğer token yoksa toolbar render edilmez
  }

  if (data && data.profil && !data.profil.is_ogretmen) {
    return null
  }

  if (loading) {
    return <Spinner showSpinner={loading} color="red" />
  }

  if (error) {
    console.error(error)
    navigate('/')
  }
  const handleSubeChange = (value: number) => {
    if (setSinif) {
      setSinif(value)
    }
  }
  const handleLogout = () => {
    clearLoginSession()
    setToken(DEFAULT_API_TOKEN)
    setAuthState(AuthState.LOGGED_OUT)
    navigate('/') // Ana sayfaya yönlendirme
  }

  return (
    <div
      style={{
        //sonradan renk1 ve renk2 olarak değiştir.
        background: `linear-gradient(to bottom, ${renk1 || '#fff'} 0%, ${renk1 || '#fff'} 93%,  ${
          renk2 || '#ccc'
        } 93%)`,
      }}
      className="okul-yonetim-toolbar"
    >
      {isMobile ? (
        <div className="okul-yonetim-toolbar-content-mobile">
          <div className="mobile-toolbar-header">
            <div className="campus-title">
              <Link className="okul-yonetim-link" to={`${AppRoutes.okulyonetim.base}/`}>
                <img className="okulyonetim-logo" src={logo} alt="logo" />
              </Link>
              <span className="campus-title-text">Kampüs</span>
              <Select defaultValue={subeOptions[0]?.value} options={subeOptions} onChange={handleSubeChange} />
            </div>
          </div>
          <div className="okulyonetim-account">
            <div className="account-info">
              <UserIcon onClick={() => window.open(`/v1/${AppRoutes.account.myAccountInformation}`)} />
              <div className="name">
                <span>Hoşgeldiniz</span>
                <span>{data?.profil?.isim}</span>
              </div>
            </div>
            <div className="favorite-info">
              <Link to={`${AppRoutes.okulyonetim.base}/${AppRoutes.okulyonetim.favoriteProducts}`}>
                <HeartOutlined />
              </Link>
            </div>
            <div className="basket-info">
              <Link to={`${AppRoutes.okulyonetim.base}/${AppRoutes.okulyonetim.addedToClass}`}>
                <ShoppingCartOutlined />
                <div className="add-class-title">Sınıfa Eklenenler</div>
              </Link>
            </div>
            <div className="log-out">
              <LogoutOutlined onClick={handleLogout} />
            </div>
          </div>
        </div>
      ) : (
        <div className="okul-yonetim-toolbar-content">
          <div className="okul-yonetim-toolbar-main">
            <Link className="okul-yonetim-link" to={`${AppRoutes.okulyonetim.base}/`}>
              <img className="okulyonetim-logo" src={logo} alt="logo" />
            </Link>
            <div className="campus-content">
              <div className="campus-header">
                <div className="campus-title">
                  <CampusIcon /> Bulunduğunuz Kampüs / Sınıf Seviyeniz
                </div>
                <div className="contact-info">
                  <div>
                    <img width={30} height={30} src={icon} alt="" />
                    <span className="call-center">
                      {' '}
                      {yetkili_ad} {yetkili_soyad}{' '}
                    </span>
                    <a href={`tel:${yetkili_numara}`}>{yetkili_numara}</a>
                  </div>
                  <div>
                    <MailFilled color="white" />
                    <a href="mailto:urundestek@okulsepeti.com.tr">urundestek@okulsepeti.com.tr</a>
                  </div>
                </div>
              </div>
              <div className="campus-content-down">
                <div className="ant-select-div">
                  <Select
                    suffixIcon={
                      <>
                        Değiştir <CaretDownFilled />{' '}
                      </>
                    }
                    size="large"
                    defaultValue={subeOptions[0]?.value}
                    options={subeOptions}
                    onChange={handleSubeChange}
                  />
                </div>
                <div className="okulyonetim-account">
                  <div className="account-info">
                    <UserIcon
                      onClick={() =>
                        (window.location.href = `/v1/${AppRoutes.account.root}/${AppRoutes.account.myAccountInformation}`)
                      }
                    />
                    <div className="name">
                      <span>Hoşgeldiniz</span>
                      <span>{data?.profil?.isim}</span>
                    </div>
                  </div>
                  <div className="favorite-info">
                    <Link to={`${AppRoutes.okulyonetim.base}/${AppRoutes.okulyonetim.favoriteProducts}`}>
                      <HeartOutlined />
                      {/*<span className="total-favorite-product">{data?.profil?.toplamlar?.toplam_favori_urun}</span>*/}
                    </Link>
                  </div>
                  <div className="basket-info">
                    <Link to={`${AppRoutes.okulyonetim.base}/${AppRoutes.okulyonetim.addedToClass}`}>
                      <ShoppingCartOutlined />
                      <div className="add-class-title">Sınıfa Eklenenler</div>
                      {/*<span className="total-class-header">*/}
                      {/*<Tooltip title="Toplam Sınıf Sayısı">*/}
                      {/*    <span className='total-class-product'>{data?.profil?.toplamlar?.toplam_sinif}</span>*/}
                      {/*  </Tooltip>*/}
                      {/*  <Tooltip title="Toplam Sınıf Ders Sayısı">*/}
                      {/*    <span className='total-class-product'>{data?.profil?.toplamlar?.toplam_sinif_urunu}</span>*/}
                      {/*  </Tooltip>*/}
                      {/*</span>*/}
                    </Link>
                  </div>
                  <div className="log-out">
                    <LogoutOutlined onClick={handleLogout} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default OkulyonetimToolbar
